"use client";

import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import useVisibility from "@/utils/hooks/use-visibility";
import { Suspense, useEffect } from "react";
import type { BusRequest } from "@/types/busrequest";
import { Company } from "@/types/company";
import { AdvertiserLocation } from "@/types/location";
import dynamic from "next/dynamic";
import { useTranslation } from "@/app/i18n/client";
import { MarkerType } from "@/components/Util/Map/Map";
import { cn } from "@/lib/utils";
import { useGoogleMaps } from "@/utils/google-maps-context";

const Map = dynamic(
  () => import(/* webpackChunkName: "map" */ "@/components/Util/Map/Map"),
);

export default function MapContainer(props: {
  showClusterModal: (ids: string[]) => void;
  showSingleMarkerModal?: (id: string) => void;
  visibleItems: (BusRequest | AdvertiserLocation | Company)[];
  type: MarkerType;
  height: string;
  hideSearchField?: boolean;
  className?: string;
  containerClassName?: string;
  searchFieldClassName?: string;
}) {
  const { t } = useTranslation();
  const { isLoaded: isMapsLibLoaded, loadNow } = useGoogleMaps();

  const [isVisible, currentElement] = useVisibility<HTMLDivElement>();

  useEffect(() => {
    if (isVisible) {
      loadNow();
    }
  }, [loadNow, isVisible]);

  return (
    <div ref={currentElement} className={cn("map-container", props.className)}>
      {isVisible && isMapsLibLoaded ? (
        <Suspense
          fallback={
            <div
              className={cn("google-map-container", props.containerClassName)}
            >
              <LoadingSpinner
                embedded={true}
                label={t("LOADING.LABEL.MAP") ?? "Map"}
              />
            </div>
          }
        >
          <Map
            {...props}
            className={props.containerClassName}
            searchFieldClassName={props.searchFieldClassName}
          />
        </Suspense>
      ) : (
        <div className={cn("google-map-container", props.containerClassName)}>
          <LoadingSpinner
            embedded={true}
            label={t("LOADING.LABEL.MAP") ?? "Map"}
          />
        </div>
      )}
    </div>
  );
}
